import { isLabelContentType, LabelCategory } from '@tableau/catalog-ui-temp';
import { LabelsDialog, LabelsDialogProps } from '@tableau/data-catalog-shared';
import React from 'react';
import { call, put } from 'redux-saga/effects';

import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { SetLabelsAction } from '~src/catalog/actions/labels/SetLabelsAction';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { CatalogTelemetryProvider } from '~src/catalog/DataCatalogTelemetry';
import { Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { assertUnreachable } from '~src/commonInfra/utils/TypescriptHelpers';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { AllActionsAllowed } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';
import { selectAvailableActionsForEntity } from '~src/spacesPlatform/selectors/EntityActionsSelectors';


export function* handleSetLabelsAction(action: SetLabelsAction) {
  try {
    yield call(openLabelsDialog, action);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openLabelsDialog(action: SetLabelsAction) {
  const contentType = action.item.contentType;
  if (!isLabelContentType(contentType)) {
    throw new Error(`${contentType} `);
  }
  const contentItem: LabelsDialogProps['contentItem'] = {
    contentId: action.item.luid,
    contentType,
    contentName: action.item.name,
  };

  const initialCategory = getInitialCategory(action);

  const capabilities = getAvailableSetLabelsActions(action.item);

  return ReactModalService.openCustom(({ close }) => (
    <CatalogProviders>
      <CatalogTelemetryProvider>
        <LabelsDialog
          capabilities={capabilities}
          contentItem={contentItem}
          hideDialog={close}
          initialCategory={initialCategory?.toUpperCase()}
          getAnchorClientRect={undefined}
          />
      </CatalogTelemetryProvider>
    </CatalogProviders>
  )).result;
}

function getAvailableSetLabelsActions(entity: IExplorableItemTypeInfo): LabelsDialogProps['capabilities'] {
  const state = getAppStore().getState();

  const catalogEnabled = SessionSiteSelectors.catalogingOnSiteEnabled(state);
  const availableActions = selectAvailableActionsForEntity(state, entity) ?? AllActionsAllowed;

  const certification = 'setCertification' in availableActions ? availableActions.setCertification : false;
  const warning = 'updateRemoveDataQuality' in availableActions ? availableActions.updateRemoveDataQuality && catalogEnabled : false;

  return { certification, warning };
}

function getInitialCategory(action: SetLabelsAction): LabelCategory | undefined {
  switch (action.type) {
    case 'SetCustomLabel': return undefined;
    case 'SetAllLabels': return undefined;
    case 'SetCertification': return 'certification';
    case 'SetSensitiveLabel': return 'sensitivity';
    case 'SetDataQuality': return 'warning';
    case 'SetAiLabel': return 'ai';
    default:
      assertUnreachable(action.type);
  }
}
